import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BadgeCheck, Download, FileWarning, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { getCertificate } from "@/lib/certificates.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const TITLE = "Verify an AI Inspection Certificate | BidNova Auctions";
const DESCRIPTION =
  "Check that a BidNova AI Inspection Certificate is genuine, unedited and still valid for the listing it was issued against.";

export const Route = createFileRoute("/verify/$code")({
  head: ({ params }) => {
    const url = `https://bidnovaauctions.uk/verify/${params.code}`;
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: VerifyCertificatePage,
});

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-6 border-b border-border/40 py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function Score({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100);
  const tone = pct >= 80 ? "bg-emerald-500" : pct >= 55 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="capitalize text-muted-foreground">{label}</span>
        <span className="font-medium">{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VerifyCertificatePage() {
  const { code } = Route.useParams();
  const fetchCert = useServerFn(getCertificate);
  const { data, isPending, isError } = useQuery({
    queryKey: ["certificate", code],
    queryFn: () => fetchCert({ data: { code } }),
    staleTime: 60_000,
  });

  const pdfHref = `/api/public/certificates/${encodeURIComponent(code.toUpperCase())}/pdf`;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <header className="mb-8 text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Certificate verification</p>
        <h1 className="mt-2 font-display text-3xl text-white sm:text-4xl">
          AI Inspection Certificate
        </h1>
        <p className="mt-2 font-mono text-sm text-muted-foreground">{code.toUpperCase()}</p>
      </header>

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Checking the platform record…
        </div>
      ) : isError || !data ? (
        <Card className="border-destructive/40 p-6 text-center">
          <FileWarning className="mx-auto mb-3 h-8 w-8 text-destructive" aria-hidden />
          <h2 className="text-lg font-semibold">Certificate not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            No BidNova certificate exists with this code. If you were given a PDF, it was not issued
            by BidNova or the code was mistyped.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card
            className={`p-6 ${
              !data.signature_valid
                ? "border-destructive/50"
                : data.revoked
                  ? "border-amber-500/50"
                  : "border-emerald-500/40"
            }`}
          >
            <div className="flex items-start gap-4">
              {!data.signature_valid ? (
                <ShieldAlert className="h-8 w-8 shrink-0 text-destructive" aria-hidden />
              ) : data.revoked ? (
                <FileWarning className="h-8 w-8 shrink-0 text-amber-500" aria-hidden />
              ) : (
                <ShieldCheck className="h-8 w-8 shrink-0 text-emerald-500" aria-hidden />
              )}
              <div>
                <h2 className="text-lg font-semibold">
                  {!data.signature_valid
                    ? "Signature does not match"
                    : data.revoked
                      ? "Expired certificate"
                      : "Genuine and valid"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {!data.signature_valid
                    ? "This record failed its integrity check and must not be relied on."
                    : data.revoked
                      ? data.revoked_reason === "photos_changed"
                        ? "The listing photographs were changed after this report was issued, so it no longer describes the item on sale. The seller must run a new AI scan."
                        : "This certificate has been withdrawn."
                      : "This certificate was issued by BidNova and matches our records exactly. Any PDF whose details differ from this page has been edited."}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[11px]">
                    Signature {data.signature_fingerprint}
                  </Badge>
                  <Badge variant="outline" className="text-[11px]">
                    Issued {new Date(data.issued_at).toLocaleString("en-GB")}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild size="sm">
                <a href={pdfHref}>
                  <Download className="mr-2 h-4 w-4" aria-hidden /> Download PDF
                </a>
              </Button>
              {data.auction_id ? (
                <Button asChild size="sm" variant="outline">
                  <Link to="/auctions/$id" params={{ id: data.auction_id }}>
                    View the listing
                  </Link>
                </Button>
              ) : null}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gold">
              <BadgeCheck className="h-4 w-4" aria-hidden /> Card identity
            </h3>
            <Row label="Title" value={data.payload.identity.title} />
            <Row label="Card" value={data.payload.identity.card_name} />
            <Row label="Set" value={data.payload.identity.set_name} />
            <Row label="Number" value={data.payload.identity.card_number} />
            <Row label="Rarity" value={data.payload.identity.rarity} />
            <Row label="Language" value={data.payload.identity.language} />
            <Row label="Edition" value={data.payload.identity.edition} />
            <Row label="Finish" value={data.payload.identity.finish} />
            <Row label="Year" value={data.payload.identity.release_year} />
            <Row
              label="Grading"
              value={
                data.payload.identity.graded
                  ? [data.payload.identity.grading_company, data.payload.identity.grade]
                      .filter(Boolean)
                      .join(" ")
                  : "Raw / ungraded"
              }
            />
          </Card>

          <Card className="p-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gold">
              Condition assessment
            </h3>
            <Row label="Suggested condition" value={data.payload.condition.category} />
            {data.payload.condition.score != null ? (
              <Row label="Overall health" value={`${data.payload.condition.score}/100`} />
            ) : null}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {Object.entries(data.payload.condition.subscores).map(([k, v]) => (
                <Score key={k} label={k} value={v} />
              ))}
            </div>
            {data.payload.centering ? (
              <div className="mt-5 border-t border-border/40 pt-4">
                <Row label="Centering (horizontal)" value={data.payload.centering.horizontal} />
                <Row label="Centering (vertical)" value={data.payload.centering.vertical} />
              </div>
            ) : null}
          </Card>

          {data.payload.defects.length > 0 ? (
            <Card className="p-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gold">
                Defect summary
              </h3>
              <ul className="space-y-2 text-sm">
                {data.payload.defects.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[11px] capitalize">
                      {d.severity ?? "noted"}
                    </Badge>
                    <span className="capitalize">{d.type ?? "defect"}</span>
                    {d.image_role ? (
                      <span className="text-muted-foreground">({d.image_role})</span>
                    ) : null}
                    {d.description ? (
                      <span className="text-muted-foreground">— {d.description}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {Object.keys(data.payload.confidences).length > 0 ? (
            <Card className="p-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gold">
                Confidence scores
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(data.payload.confidences).map(([k, v]) => (
                  <Score key={k} label={k} value={v} />
                ))}
              </div>
            </Card>
          ) : null}

          <p className="text-xs leading-relaxed text-muted-foreground">{data.payload.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
